/**
 * Blocker that maintains a set of events that are blocked from occurring more than once within a provided time-window.
 */
export class TimeBasedEventBlocker<TKey extends string | number = never> {
  /** Events that are (temporarily) blocked from occurring. */
  private readonly blockedEvents = new Map<TKey, number>();

  /** Event logging callbacks to invoke when an aggregated batch block expires */
  private readonly eventCallbacks = new Map<TKey, () => void>();

  /** Map to keep track of events that are (temporarily) blocked from occurring and their aggregated hit counts. */
  private readonly eventHitCounts = new Map<TKey, number>();

  /** Map to keep track of event durations [min, max, avg]. */
  private readonly eventDurations = new Map<TKey, number[]>();

  /** Map to keep track of all durations, we will store up to 10 numbers here, and then transition to approximate method. */
  private readonly eventDurationsTotal = new Map<TKey, number[]>();

  /** Id of the timer responsible for periodically flushing blocked events */
  private batchTimerId: number | undefined = undefined;

  /**
   * Whether the provided event is currently blocked from occurring.
   *
   * IMPORTANT: This method may be called from super high-hitting code paths (like the typing loop), so please keep
   * performance considerations into account when refactoring it!
   *
   * @param key  Unique identifier of event. IMPORTANT: Avoid collisions or you may end up with unexpected behaviors.
   */
  public isEventBlocked(key: TKey, callback?: () => void): boolean {
    const blockedExpiryTime = this.blockedEvents.get(key);

    if (!blockedExpiryTime) {
      return false;
    }

    if (Date.now() >= blockedExpiryTime) {
      // If expiry time is over, event is no longer blocked
      this.unblockEvent(key);

      return false;
    }

    // Perform event count aggregation while the event is blocked
    this.aggregateEventCount(key);

    if (callback) {
      this.eventCallbacks.set(key, callback);

      this.scheduleAndFlushCallbacks();
    }

    // Otherwise, event is still blocked
    return true;
  }

  private scheduleAndFlushCallbacks(): void {
    if (this.batchTimerId === undefined && this.eventCallbacks.size > 0) {
      this.batchTimerId = window.setTimeout(() => {
        const now = Date.now();

        const toDelete: TKey[] = [];

        this.eventCallbacks.forEach((cb, k) => {
          const blockedExpiryTime = this.blockedEvents.get(k);
          if (blockedExpiryTime && now >= blockedExpiryTime) {
            cb();
            toDelete.push(k);
          }
        });

        for (const k of toDelete) {
          this.blockedEvents["delete"](k);
          this.eventCallbacks["delete"](k);
        }

        this.batchTimerId = undefined;
        this.scheduleAndFlushCallbacks();
      }, 10 * 1000); // 10 seconds
    }
  }

  /**
   * Blocks the given event for the provided time window.
   *
   * @param key  Unique identifier of event. IMPORTANT: Avoid collisions or you may end up with unexpected behaviors.
   * @param durationInMs  Duration for which the provided event should be blocked.
   */
  public blockEvent(key: TKey, durationInMs: number): void {
    const expiryTime = Date.now() + durationInMs;
    this.blockedEvents.set(key, expiryTime);

    // Add event to map for aggregation and set the hit count to 0
    this.updateEventHitCount(key, 0);
    this.eventDurations["delete"](key); // delete is not supported by ajaxminmap
    this.eventDurationsTotal["delete"](key); // delete is not supported by ajaxminmap
  }

  /**
   * Unblocks the given event.
   *
   * @param key  Unique identifier of event. IMPORTANT: Avoid collisions or you may end up with unexpected behaviors.
   */
  public unblockEvent(key: TKey): void {
    // AjaxMin and AjaxMinMap don't recognize delete as a method and throw when parsing Set.delete()
    // We're accessing it this way here to get around this limitation
    this.blockedEvents["delete"](key);
  }

  /**
   * Retrieves the current hit count of the given event.
   *
   * @param key  Unique identifier of event. IMPORTANT: Avoid collisions or you may end up with unexpected behaviors.
   */
  public getEventHitCount(key: TKey): number | undefined {
    return this.eventHitCounts.get(key);
  }

  /**
   * Updates the eventHitCount map with the given key and value.
   *
   * @param key  Unique identifier of event. IMPORTANT: Avoid collisions or you may end up with unexpected behaviors.
   */
  public updateEventHitCount(key: TKey, value: number): void {
    this.eventHitCounts.set(key, value);
  }

  /**
   * If the event is in the eventHitCounts map, increment the hit count and store it.
   *
   * @param key  Unique identifier of event. IMPORTANT: Avoid collisions or you may end up with unexpected behaviors.
   */
  public aggregateEventCount(key: TKey): void {
    let hitCount = this.getEventHitCount(key);
    if (typeof hitCount === "number") {
      this.updateEventHitCount(key, ++hitCount);
    }
  }

  /**
   * Retrieves the current aggregated durations of the given event.
   *
   * @param key  Unique identifier of event. IMPORTANT: Avoid collisions or you may end up with unexpected behaviors.
   */
  public getEventDurations(key: TKey): number[] | undefined {
    return this.eventDurations.get(key);
  }

  /**
   * Keep track of event duration statistics for aggregated events.
   *
   * @param key  Unique identifier of event. IMPORTANT: Avoid collisions or you may end up with unexpected behaviors.
   * @param duration  Duration in milliseconds for the event
   */
  public aggregateEventDuration(key: TKey, duration: number): void {
    const count = (this.getEventHitCount(key) ?? 0) + 1;
    const totalDurations = this.eventDurationsTotal.get(key);
    const durations = this.eventDurations.get(key);
    if (durations !== undefined) {
      const newDurations = [...durations];
      // Min
      if (durations[0] > duration) {
        newDurations[0] = duration;
      }

      // Max
      if (durations[1] < duration) {
        newDurations[1] = duration;
      }

      // Avg
      let average;
      if (totalDurations !== undefined && totalDurations.length < 10) {
        totalDurations.push(duration);
        average = totalDurations.reduce((acc, value) => acc + value, 0) / count;
      } else {
        // Switch to approximate method, drop the numbers
        average = durations[2] + (duration - durations[2]) / count;
        this.eventDurationsTotal["delete"](key); // delete is not supported by ajaxminmap
      }
      newDurations[2] = average;

      this.eventDurations.set(key, newDurations);
    } else {
      this.eventDurations.set(key, [duration, duration, duration]);
      this.eventDurationsTotal.set(key, [duration]);
    }
  }
}

/** Common representations of milliseconds. */

export const ONE_SECOND_IN_MS = 1000;
export const ONE_MINUTE_IN_MS = 60 * ONE_SECOND_IN_MS;
export const ONE_HOUR_IN_MS = 60 * ONE_MINUTE_IN_MS;
export const ONE_DAY_IN_MS = 24 * ONE_HOUR_IN_MS;
